import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Send, Volume2, Sparkles, X, AlertCircle, Ear, EarOff, ClipboardCheck, Wallet, CreditCard, BookMarked, Trophy } from "lucide-react";
import Waveform from "./Waveform";
import { CatalogSuggestions } from "./CatalogSuggestions";
import { parseVoiceCommand, formatMXN, normalizeTTS } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  "Suma dos leches Lala y un pan Bimbo",
  "Agrega una Coca de lata y tres bolillos",
  "Cobra un kilo de tortillas y medio kilo de frijol",
  "Chalán, cierra el turno",
];

// Wake word engine — matches "chalan" / "chalán" / "oye chalan", case-insensitive.
// Returns the text spoken AFTER the wake word (empty string if none yet).
const WAKE_RE = /\b(?:oye[,\s]+)?chal(?:a|á)n\b[,.\s:!¿¡]*/i;
function stripWake(raw) {
  if (!raw) return "";
  const m = raw.match(WAKE_RE);
  if (!m) return "";
  const idx = m.index + m[0].length;
  return raw.slice(idx).trim();
}
const WAKE_TIMEOUT_MS = 5000; // Return to standby after 5s of no follow-up.
const WAKE_ARMED_KEY = "chalann_wake_armed";

export default function VoicePanel({ onItemsRecognized, onRawText, disabled }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState(null); // {tts_message, matched, unmatched, total}
  const [listening, setListening] = useState(false);
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [micSupported, setMicSupported] = useState(true);
  // Wake word "Chalan" always-listening engine.
  const [wakeArmed, setWakeArmed] = useState(() => localStorage.getItem(WAKE_ARMED_KEY) === "1");
  const [wakeActive, setWakeActive] = useState(false); // true during the 5s follow-up window
  // Owner-controlled global on/off from DashNav — when OFF, wake-word and mic are hard-disabled.
  const [chalannGloballyOn, setChalannGloballyOn] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = () => import("@/lib/api").then(({ getChalannEnabled }) =>
      getChalannEnabled().then((r) => mounted && setChalannGloballyOn(!!r.enabled)).catch(() => {})
    );
    load();
    const onChange = () => load();
    window.addEventListener("chalann:toggle-changed", onChange);
    return () => { mounted = false; window.removeEventListener("chalann:toggle-changed", onChange); };
  }, []);
  const wakeRecRef = useRef(null);
  const wakeTimerRef = useRef(null);
  const wakeBufferRef = useRef("");
  const wakeSubmittingRef = useRef(false);
  const inputRef = useRef(null);
  const utteranceRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setMicSupported(!!SR);
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* noop */ }
      }
      if (wakeRecRef.current) {
        try { wakeRecRef.current.stop(); } catch { /* noop */ }
      }
      if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
    };
  }, []);

  // Persist wake toggle
  useEffect(() => {
    localStorage.setItem(WAKE_ARMED_KEY, wakeArmed ? "1" : "0");
  }, [wakeArmed]);

  // Fire the follow-up command once the 5s window closes (or user pauses).
  const dispatchWakeCommand = () => {
    const cmd = wakeBufferRef.current.trim();
    wakeBufferRef.current = "";
    setWakeActive(false);
    if (wakeTimerRef.current) { clearTimeout(wakeTimerRef.current); wakeTimerRef.current = null; }
    if (!cmd) return; // silent — safe return to standby
    if (wakeSubmittingRef.current) return;
    wakeSubmittingRef.current = true;
    submit(cmd).finally(() => { wakeSubmittingRef.current = false; });
  };

  // Wake-word background engine. Only runs while:
  //  - Browser supports SpeechRecognition
  //  - wakeArmed = true
  //  - Manual mic (recording) is NOT active (browsers only allow one SR at a time)
  useEffect(() => {
    if (!wakeArmed || recording || !chalannGloballyOn || disabled) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    let cancelled = false;
    const rec = new SR();
    rec.lang = "es-MX";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interimBuf = "";
      let finalBuf = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalBuf += t + " ";
        else interimBuf += t + " ";
      }
      const combined = (finalBuf + interimBuf).toLowerCase();
      if (!wakeActive) {
        if (WAKE_RE.test(combined)) {
          setWakeActive(true);
          wakeBufferRef.current = stripWake(combined);
          // Play a subtle "listening" chirp via TTS-lite.
          try {
            if (window.speechSynthesis) {
              const chirp = new SpeechSynthesisUtterance("Diga, jefe.");
              chirp.lang = "es-MX"; chirp.rate = 1.15; chirp.volume = 0.6;
              window.speechSynthesis.speak(chirp);
            }
          } catch { /* noop */ }
          // Start 5s standby timer
          if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
          wakeTimerRef.current = setTimeout(dispatchWakeCommand, WAKE_TIMEOUT_MS);
        }
      } else {
        // Already listening — accumulate everything after wake word.
        const afterWake = stripWake(combined) || combined.trim();
        wakeBufferRef.current = afterWake;
        // Reset 5s timer on every fresh syllable so we don't cut the user off.
        if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
        wakeTimerRef.current = setTimeout(dispatchWakeCommand, WAKE_TIMEOUT_MS);
        // If a final result landed with actual content, fire immediately.
        if (finalBuf.trim() && afterWake.length > 2) {
          if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
          wakeTimerRef.current = setTimeout(dispatchWakeCommand, 800); // small tail
        }
      }
    };

    rec.onerror = (e) => {
      // "not-allowed" / "audio-capture" → user denied mic; stop and disarm gracefully.
      if (e.error === "not-allowed" || e.error === "audio-capture" || e.error === "service-not-allowed") {
        setWakeArmed(false);
        toast.error("Necesito permiso de micrófono para escuchar 'Chalan'");
      }
    };

    rec.onend = () => {
      // Browsers auto-stop continuous recognition. Restart while armed.
      if (cancelled || !wakeArmed) return;
      try { rec.start(); } catch { /* already running */ }
    };

    try {
      rec.start();
      wakeRecRef.current = rec;
    } catch { /* noop */ }

    return () => {
      cancelled = true;
      try { rec.stop(); } catch { /* noop */ }
      wakeRecRef.current = null;
      if (wakeTimerRef.current) { clearTimeout(wakeTimerRef.current); wakeTimerRef.current = null; }
      setWakeActive(false);
    };
  }, [wakeArmed, recording]);

  const submit = async (override) => {
    const value = (override ?? text).trim();
    if (!value) {
      toast.error("Escribe o dicta un comando");
      inputRef.current?.focus();
      return;
    }
    setLoading(true);
    setListening(true);
    setReply(null);
    try {
      const r = await parseVoiceCommand(value);
      setReply(r);
      if (onRawText) onRawText(value);
      // Refresh usage badge in DashNav
      window.dispatchEvent(new Event("chalann:voice-used"));
      // Soft cap notice — we still fell back to local match, so keep it friendly.
      if (r.capped) {
        toast("Modo ahorro IA · Cap diario alcanzado", {
          description: "Seguimos leyendo con match local. Se reinicia mañana.",
        });
      }
      // add matched to cart
      if (r.matched_items?.length) {
        onItemsRecognized?.(r.matched_items);
      }
      if (r.unmatched?.length && !r.matched_items?.length) {
        toast.error("No pude reconocer los productos");
      }
      // TTS
      speak(r.tts_message);
      setText("");
    } catch (e) {
      toast.error("No se pudo procesar el comando");
    } finally {
      setLoading(false);
      setListening(false);
    }
  };

  const speak = async (msg) => {
    try {
      if (!msg || !window.speechSynthesis) return;
      // Normalize monetary values to Mexican Spanish spoken form before speaking.
      const spoken = await normalizeTTS(msg);
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(spoken);
      u.lang = "es-MX";
      u.rate = 1.02;
      u.pitch = 1;
      u.onstart = () => setAudioPlaying(true);
      u.onend = () => setAudioPlaying(false);
      u.onerror = () => setAudioPlaying(false);
      utteranceRef.current = u;
      window.speechSynthesis.speak(u);
    } catch { /* noop */ }
  };

  const replay = () => {
    if (reply?.tts_message) speak(reply.tts_message);
  };

  const stopAudio = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setAudioPlaying(false);
  };

  const startRecording = () => {
    if (disabled) {
      toast.info("Modo escáner activo — cambia a Modo Voz para hablar.");
      return;
    }
    if (!chalannGloballyOn) {
      toast.info("Chalán está apagado. Enciéndelo desde la barra superior para hablarle.");
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Tu navegador no soporta micrófono. Usa Chrome/Edge o escribe el comando.");
      return;
    }
    try {
      const rec = new SR();
      rec.lang = "es-MX";
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      let finalText = "";
      rec.onstart = () => { setRecording(true); setInterim(""); };
      rec.onresult = (e) => {
        let interimBuf = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalText += t;
          else interimBuf += t;
        }
        setInterim(interimBuf);
        setText(finalText + interimBuf);
      };
      rec.onerror = (e) => {
        setRecording(false);
        if (e.error === "not-allowed") toast.error("Permite el micrófono para dictar");
        else if (e.error !== "no-speech" && e.error !== "aborted") toast.error("Error del micrófono");
      };
      rec.onend = () => {
        setRecording(false); setInterim("");
        const value = (finalText || text).trim();
        if (value) submit(value);
      };
      recognitionRef.current = rec;
      rec.start();
    } catch (err) {
      toast.error("No se pudo iniciar el micrófono");
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const toggleMic = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#ff5a00]/[0.14] to-transparent p-5 md:p-6"
      data-testid="voice-panel"
    >
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="chip"><span className="rec-dot" /> Comando por voz · IA</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (!micSupported) { toast.error("Tu navegador no soporta escucha continua"); return; }
              setWakeArmed((v) => !v);
              if (!wakeArmed) toast.success("Escucha activada · Di 'Chalan' cuando quieras");
              else toast("Escucha en pausa");
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[10px] font-mono uppercase tracking-widest transition-colors ${
              wakeArmed
                ? "border-[#00b3ff]/60 bg-[#00b3ff]/10 text-[#00b3ff]"
                : "border-white/10 text-white/50 hover:text-white"
            }`}
            data-testid="wake-toggle"
            title={wakeArmed ? "Escucha continua ON — di 'Chalan'" : "Activar escucha continua 'Chalan'"}
          >
            {wakeArmed ? <Ear size={12} /> : <EarOff size={12} />}
            <span>{wakeArmed ? "Escuchando 'Chalan'" : "Escucha en pausa"}</span>
          </button>
          <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase hidden md:inline">
            Claude Haiku 4.5 · español MX
          </span>
        </div>
      </div>

      {/* Wake activation banner */}
      <AnimatePresence>
        {wakeActive && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="mb-4 rounded-2xl border border-[#00b3ff]/50 bg-[#00b3ff]/10 px-4 py-3 flex items-center gap-3"
            data-testid="wake-active-banner"
          >
            <span className="relative flex items-center justify-center w-8 h-8">
              <motion.span
                className="absolute inset-0 rounded-full bg-[#00b3ff]/40"
                animate={{ scale: [1, 1.5], opacity: [0.9, 0] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              />
              <Mic size={14} className="relative text-[#00b3ff]" />
            </span>
            <p className="font-display text-base md:text-lg tracking-tight text-[#e6f8ff]" data-testid="wake-active-text">
              <span className="text-[#00b3ff] font-bold">Chalan escuchando…</span> ¿Qué se le ofrece, jefe?
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        {/* Mic button */}
        <button
          type="button"
          onClick={toggleMic}
          className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
            wakeActive
              ? "border-[#00b3ff] bg-[#00b3ff]/20 shadow-[0_0_40px_rgba(0,179,255,0.5)]"
              : recording
                ? "border-[#ccff00] bg-[#ccff00]/15"
                : listening
                  ? "border-[#ccff00] bg-[#ccff00]/10"
                  : "border-[#ff5a00] bg-[#ff5a00]/10 hover:bg-[#ff5a00]/20"
          }`}
          data-testid="mic-button"
          title={recording ? "Toca para detener" : (micSupported ? "Toca para dictar" : "Micrófono no soportado — escribe el comando")}
        >
          <Mic
            size={36}
            className={
              wakeActive ? "text-[#00b3ff]" : (recording || listening ? "text-[#ccff00]" : "text-[#ff5a00]")
            }
            strokeWidth={2}
          />
          {(recording || listening || wakeActive) && (
            <>
              <motion.span
                className={`absolute inset-0 rounded-full border-2 ${wakeActive ? "border-[#00b3ff]" : "border-[#ccff00]"}`}
                animate={{ scale: [1, 1.35], opacity: [0.8, 0] }}
                transition={{ duration: 1.4, repeat: Infinity }}
              />
              <motion.span
                className={`absolute inset-0 rounded-full border-2 ${wakeActive ? "border-[#00b3ff]" : "border-[#ccff00]"}`}
                animate={{ scale: [1, 1.35], opacity: [0.8, 0] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: 0.5 }}
              />
            </>
          )}
          {recording && (
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#ccff00] text-black text-[9px] font-mono uppercase tracking-widest font-bold whitespace-nowrap">
              REC · toca para parar
            </span>
          )}
        </button>

        {/* Command input */}
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="flex-1 min-w-0"
        >
          <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">
            {recording ? "Te estoy escuchando…" : (micSupported ? "Presiona el micrófono o escribe el comando" : "Escribe (o pega) el comando de voz")}
          </label>
          <div className="mt-2 flex items-center gap-2 bg-black/50 border border-white/10 rounded-2xl px-3 py-2 focus-within:border-[#ff5a00] transition-colors">
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='Ej. "Suma dos leches Lala y un pan Bimbo"'
              disabled={disabled}
              className="flex-1 bg-transparent outline-none text-white placeholder-white/30 text-sm md:text-base"
              data-testid="voice-input"
            />
            {text && (
              <button type="button" onClick={() => setText("")} className="text-white/40 hover:text-white p-1">
                <X size={14} />
              </button>
            )}
            <button
              type="submit"
              disabled={loading || disabled}
              className="rounded-full bg-[#ff5a00] hover:bg-[#ff7a30] text-black py-1.5 px-3 text-xs font-bold uppercase tracking-widest disabled:opacity-60 flex items-center gap-1"
              data-testid="voice-submit"
            >
              {loading ? "…" : (<><Send size={12} /> Enviar</>)}
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setText(ex); submit(ex); }}
                className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 text-white/60 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                data-testid={`voice-example-${i}`}
              >
                {i === 0 ? "Ej A" : i === 1 ? "Ej B" : i === 2 ? "Kilo" : "Snack"}
              </button>
            ))}
          </div>
        </form>
      </div>

      {/* Assistant audio reply bubble */}
      <AnimatePresence>
        {reply && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="mt-5 rounded-2xl border border-[#ccff00]/40 bg-black/50 backdrop-blur-md p-4"
            data-testid="voice-reply"
          >
            <div className="flex items-center gap-3">
              <button
                onClick={audioPlaying ? stopAudio : replay}
                className="w-12 h-12 rounded-full bg-[#ccff00] text-black flex items-center justify-center flex-shrink-0"
                data-testid="voice-replay"
                title={audioPlaying ? "Detener" : "Reproducir"}
              >
                {audioPlaying ? <span className="text-xl">■</span> : <Volume2 size={20} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Sparkles size={11} className="text-[#ccff00]" />
                  <span className="font-mono text-[10px] tracking-widest text-white/60 uppercase">
                    CHALANN dice
                  </span>
                  <span
                    className="font-mono text-[9px] tracking-widest text-white/40 uppercase"
                    data-testid="voice-token-badge"
                    title="Tokens IA usados en este comando"
                  >
                    · {reply.tokens_used_estimate ?? 0} tk · {reply.engine || "local"}
                  </span>
                </div>
                <p className="font-display text-lg md:text-xl tracking-tight leading-snug" data-testid="voice-tts-text">
                  {reply.tts_message}
                </p>
              </div>
              <div className="hidden md:block w-28">
                <Waveform active={audioPlaying} />
              </div>
            </div>

            {reply.intent === "shift_close" && reply.shift_close && (
              <ShiftCloseCard data={reply.shift_close} />
            )}

            {reply.matched_items?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2">
                {reply.matched_items.map((it, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/30 text-[11px]">
                    {it.is_bulk ? (
                      <>
                        <span className="text-[#ccff00] font-bold">{(it.weight_kg ?? it.quantity).toFixed(3)} kg</span>
                        <span className="text-white/90">{it.name}</span>
                        <span className="font-mono text-white/60">{formatMXN(it.subtotal)}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-[#ccff00] font-bold">+{it.quantity}</span>
                        <span className="text-white/90">{it.name}</span>
                        <span className="font-mono text-white/60">{formatMXN(it.subtotal)}</span>
                      </>
                    )}
                  </span>
                ))}
              </div>
            )}
            {reply.unmatched?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-start gap-2 text-[12px] text-[#ff5a00]">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <p>
                    No encontré en tu inventario: <strong>{reply.unmatched.map((u) => u.query).join(", ")}</strong>.
                    {reply.suggestions?.length ? " Elige abajo la presentación correcta." : " Agrégalo desde Inventario para que CHALANN lo aprenda."}
                  </p>
                </div>
              </div>
            )}
            <CatalogSuggestions
              suggestions={reply.suggestions}
              onAdded={(item) => { onItemsRecognized?.([item]); speak(`Listo jefe, sumé ${item.name}.`); }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


function ShiftCloseCard({ data }) {
  const { count = 0, total = 0, cash = 0, card = 0, credit = 0, avg_ticket = 0, top_products = [] } = data || {};
  return (
    <div
      className="mt-4 pt-4 border-t border-white/10"
      data-testid="shift-close-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck size={14} className="text-[#ccff00]" />
        <span className="font-mono text-[10px] tracking-widest text-white/60 uppercase">
          Corte del día
        </span>
        <span className="ml-auto font-mono text-[10px] tracking-widest text-white/40 uppercase" data-testid="shift-close-count">
          {count} {count === 1 ? "ticket" : "tickets"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatTile
          label="Total"
          value={formatMXN(total)}
          accent="#ccff00"
          testid="shift-close-total"
        />
        <StatTile
          icon={Wallet}
          label="Efectivo"
          value={formatMXN(cash)}
          testid="shift-close-cash"
        />
        <StatTile
          label="Ticket promedio"
          value={formatMXN(avg_ticket)}
          muted
          testid="shift-close-avg"
        />
        <StatTile
          icon={CreditCard}
          label="Tarjeta"
          value={formatMXN(card)}
          muted
          testid="shift-close-card-amt"
        />
        <StatTile
          icon={BookMarked}
          label="Fiado"
          value={formatMXN(credit)}
          muted
          testid="shift-close-credit"
        />
        <StatTile
          label="Productos"
          value={String(top_products.length)}
          muted
          testid="shift-close-topcount"
        />
      </div>

      <div className="rounded-2xl bg-black/40 border border-white/5 p-3" data-testid="shift-close-top">
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={12} className="text-[#ccff00]" />
          <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase">
            Top del día
          </span>
        </div>
        {top_products.length === 0 ? (
          <p className="text-white/40 text-sm">Sin ventas todavía.</p>
        ) : (
          <ol className="space-y-1.5">
            {top_products.map((p, i) => (
              <li
                key={p.product_id || p.name || i}
                className="flex items-center gap-3 text-sm"
                data-testid={`shift-close-top-${i}`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    i === 0
                      ? "bg-[#ccff00] text-black"
                      : "bg-white/10 text-white/80"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="text-white/90 flex-1 truncate">{p.name}</span>
                <span className="font-mono text-[11px] text-white/50 flex-shrink-0">
                  {p.quantity} · {formatMXN(p.revenue)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent, muted, testid }) {
  return (
    <div
      className={`rounded-xl border p-2.5 ${
        accent
          ? "border-[#ccff00]/40 bg-[#ccff00]/[0.06]"
          : "border-white/5 bg-black/30"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={10} className={accent ? "text-[#ccff00]" : "text-white/40"} />}
        <span className="font-mono text-[9px] tracking-widest text-white/50 uppercase truncate">
          {label}
        </span>
      </div>
      <p
        className={`font-satoshi tracking-tight ${
          accent ? "text-[#ccff00] text-lg" : muted ? "text-white/80 text-sm" : "text-white text-base"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
